// NewAiPrompt/aiResult.js
//
// Pure helpers for the AI Assistant popover. Two jobs, both born from measured
// UX gaps in the funnel coverage map:
//
//   1. Turn a raw completion/titles API response into the single display string,
//      and report when that string is empty — so an empty AI reply produces a
//      "no suggestions" message instead of a SILENT no-op (blank box / empty
//      chat bubble). (funnel [!] "Empty result — nothing renders, loader off".)
//
//   2. A guarded clipboard write that only reports success when the write
//      actually landed — so the popover never flashes "Copied" when the browser
//      blocked the write (Safari / insecure origin / no Clipboard API).
//
// Kept as standalone pure functions so the logic behind those bugs is unit
// tested without mounting the whole popover.

// Extract the assistant text from an API response payload.
//   isTitle === true  -> titles/suggestions shape: { suggestions: [{ text }, ...] }
//   isTitle === false -> completion shape: { completion: "..." } or a bare string.
// Always returns a string ("" when nothing usable is present).
export function extractAiText(responseData, isTitle) {
  if (responseData == null) return "";

  if (isTitle) {
    const suggestions = responseData.suggestions;
    if (Array.isArray(suggestions) && suggestions.length > 0) {
      const text = suggestions[0] && suggestions[0].text;
      return typeof text === "string" ? text : "";
    }
    return "";
  }

  const completion =
    responseData.completion != null ? responseData.completion : responseData;
  return typeof completion === "string" ? completion : "";
}

// True when the extracted text is missing or whitespace-only — the caller should
// surface a "no result" message rather than render an empty container/bubble.
export function isEmptyAiText(text) {
  return typeof text !== "string" || text.trim() === "";
}

export const NO_RESULT_MESSAGE =
  "No suggestions came back — try rephrasing your prompt and generate again.";

// Guarded clipboard write. Resolves true only when the text was actually copied;
// resolves false when there is nothing to copy, the Clipboard API is absent
// (insecure origin / older browser), or the browser rejects the write (Safari
// permission, etc.). Never throws.
export async function copyToClipboard(value) {
  if (typeof value !== "string" || value.trim() === "") return false;
  try {
    if (
      typeof navigator !== "undefined" &&
      navigator.clipboard &&
      typeof navigator.clipboard.writeText === "function"
    ) {
      await navigator.clipboard.writeText(value);
      return true;
    }
    return false;
  } catch (e) {
    return false;
  }
}
